/**
 * Service Firebase pour les notifications en temps réel
 * Remplace le polling HTTP par des listeners Firebase
 */

import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  getDoc,
  setDoc
} from 'firebase/firestore';

class NotificationFirebaseService {
  /**
   * Écoute en temps réel le nombre de notifications non lues
   * @param {string} userId - ID de l'utilisateur
   * @param {function} callback - Fonction appelée avec le nombre de notifications non lues
   * @returns {function} Fonction pour se désabonner du listener
   */
  static subscribeToUnreadCount(userId, callback) {
    if (!userId) {
      console.warn('NotificationFirebaseService: userId manquant');
      return () => {};
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const q = query(
        notificationsRef,
        where('opened', '==', false) // ✅ Utiliser 'opened' au lieu de 'read'
      );

      // Listener en temps réel
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const unreadCount = snapshot.size;
          callback(unreadCount);
        },
        (error) => {
          console.error('Erreur listener notifications:', error);
          callback(0); // En cas d'erreur, retourner 0
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Erreur création listener notifications:', error);
      return () => {};
    }
  }

  /**
   * Écoute en temps réel toutes les notifications d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {function} callback - Fonction appelée avec la liste des notifications
   * @param {number} maxResults - Nombre maximum de notifications à récupérer
   * @returns {function} Fonction pour se désabonner du listener
   */
  static subscribeToNotifications(userId, callback, maxResults = 50) {
    if (!userId) {
      console.warn('NotificationFirebaseService: userId manquant');
      return () => {};
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const q = query(
        notificationsRef,
        orderBy('sentAt', 'desc'), // ✅ Utiliser 'sentAt' au lieu de 'createdAt'
        limit(maxResults)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const notifications = [];
          snapshot.forEach((doc) => {
            notifications.push({
              id: doc.id,
              ...doc.data()
            });
          });
          callback(notifications);
        },
        (error) => {
          console.error('Erreur listener liste notifications:', error);
          callback([]);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Erreur création listener liste notifications:', error);
      return () => {};
    }
  }

  /**
   * Récupère toutes les notifications d'un utilisateur (une seule fois)
   * @param {string} userId - ID de l'utilisateur
   * @param {number} maxResults - Nombre maximum de notifications
   * @returns {Promise<Array>} Liste des notifications
   */
  static async getNotifications(userId, maxResults = 50) {
    if (!userId) {
      throw new Error('userId manquant');
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const q = query(
        notificationsRef,
        orderBy('sentAt', 'desc'), // ✅ Utiliser 'sentAt' au lieu de 'createdAt'
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      const notifications = [];
      
      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return notifications;
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
      throw error;
    }
  }

  /**
   * Marque une notification comme lue
   * @param {string} notificationId - ID de la notification
   * @returns {Promise<void>}
   */
  static async markAsRead(userId, notificationId) {
    if (!userId || !notificationId) {
      throw new Error('userId ou notificationId manquant');
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationRef = doc(db, 'users', userId, 'notification_history', notificationId);
      await updateDoc(notificationRef, {
        opened: true, // ✅ Utiliser 'opened' au lieu de 'read'
        openedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Erreur marquage notification comme lue:', error);
      throw error;
    }
  }

  /**
   * Marque toutes les notifications d'un utilisateur comme lues
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<number>} Nombre de notifications marquées comme lues
   */
  static async markAllAsRead(userId) {
    if (!userId) {
      throw new Error('userId manquant');
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const q = query(
        notificationsRef,
        where('opened', '==', false) // ✅ Utiliser 'opened' au lieu de 'read'
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 0;
      }

      const batch = writeBatch(db);
      const openedAt = new Date().toISOString();

      snapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, {
          opened: true, // ✅ Utiliser 'opened' au lieu de 'read'
          openedAt: openedAt
        });
      });

      await batch.commit();
      return snapshot.size;
    } catch (error) {
      console.error('Erreur marquage toutes notifications comme lues:', error);
      throw error;
    }
  }

  /**
   * Récupère le nombre de notifications non lues (une seule fois)
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<number>} Nombre de notifications non lues
   */
  static async getUnreadCount(userId) {
    if (!userId) {
      throw new Error('userId manquant');
    }

    try {
      // ✅ Utiliser la même structure que NotificationHistory
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const q = query(
        notificationsRef,
        where('opened', '==', false) // ✅ Utiliser 'opened' au lieu de 'read'
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Erreur récupération nombre notifications non lues:', error);
      throw error;
    }
  }

  /**
   * Récupère les paramètres de notification d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} Paramètres de notification
   */
  static async getNotificationSettings(userId) {
    if (!userId) {
      throw new Error('userId manquant');
    }

    try {
      const settingsRef = doc(db, 'users', userId, 'settings', 'Notifications');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        return settingsDoc.data();
      } else {
        // Retourner les valeurs par défaut si le document n'existe pas
        return {
          enabled: true,
          preferredTime: '09:00',
          timezone: 'Africa/Casablanca'
        };
      }
    } catch (error) {
      console.error('Erreur récupération paramètres notifications:', error);
      throw error;
    }
  }

  /**
   * Met à jour les paramètres de notification d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} settings - Paramètres à sauvegarder
   * @returns {Promise<void>}
   */
  static async updateNotificationSettings(userId, settings) {
    if (!userId) {
      throw new Error('userId manquant');
    }

    if (!settings) {
      throw new Error('settings manquant');
    }

    try {
      const settingsRef = doc(db, 'users', userId, 'settings', 'Notifications');
      
      // Ajouter le timestamp de mise à jour
      const settingsWithTimestamp = {
        ...settings,
        updatedAt: new Date().toISOString()
      };

      await setDoc(settingsRef, settingsWithTimestamp, { merge: true });
    } catch (error) {
      console.error('Erreur mise à jour paramètres notifications:', error);
      throw error;
    }
  }
}

export default NotificationFirebaseService;
